
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { ScheduledMatch } from '@/types/scheduledMatch';
import { ScheduledMatchCard } from '@/components/scheduled/ScheduledMatchCard';
import { Carousel, CarouselContent, CarouselItem, CarouselDots } from '@/components/ui/carousel';
import { ROUTES } from '@/constants/routes';

interface UpcomingMatchesWidgetProps {
  matches: ScheduledMatch[];
  loading: boolean;
}

export const UpcomingMatchesWidget: React.FC<UpcomingMatchesWidgetProps> = ({
  matches,
  loading
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  if (loading) {
    return (
      <Card className="border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center">
            <Calendar className="h-4 w-4 mr-2 text-primary" />
            {t('upcomingMatches.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2].map((i) => (
              <div key={i} className="animate-pulse bg-muted rounded-lg h-20"></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (matches.length === 0) {
    return null;
  }

  return (
    <Card className="border-border">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center">
            <Calendar className="h-4 w-4 mr-2 text-primary" />
            {t('upcomingMatches.title')}
            {matches.length > 1 && (
              <span className="ml-1 text-muted-foreground">({matches.length})</span>
            )}
          </CardTitle>
          {matches.length > 2 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(ROUTES.MATCHES)}
              className="text-primary hover:text-primary/90 text-xs"
            >
              {t('upcomingMatches.viewAll')} <ArrowRight className="h-3 w-3 ml-1" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
        <Carousel className="w-full" opts={{ align: "start", loop: false }}>
          <CarouselContent className="-ml-2 md:-ml-4">
            {matches.map((match) => (
              <CarouselItem key={match.id} className="pl-2 md:pl-4 basis-full md:basis-1/2">
                <ScheduledMatchCard match={match} />
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselDots />
        </Carousel>
      </CardContent>
    </Card>
  );
};
